import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Typography,
  Switch,
  Image,
  InputNumber,
  Avatar,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  TrophyOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { tradersAPI, getImageUrl } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;

const Traders = () => {
  const [loading, setLoading] = useState(false);
  const [traders, setTraders] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTrader, setEditingTrader] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchTraders();
  }, [pagination.current, pagination.pageSize]);

  const fetchTraders = async () => {
    setLoading(true);
    try {
      const response = await tradersAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize
      });
      if (response.data.success) {
        setTraders(response.data.data.traders);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取交易员列表失败');
      console.error('Fetch traders error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTrader(null);
    form.resetFields();
    form.setFieldsValue({ 
      isActive: true, 
      totalProfit: 0,
      totalTrades: 0,
      winRate: 0,
      monthlyProfit: 0,
      followers: 0,
      likes: 0,
      rank: traders.length + 1
    });
    setModalVisible(true);
  };

  const handleEdit = (trader) => {
    setEditingTrader(trader);
    form.setFieldsValue({
      ...trader,
      totalProfit: parseFloat(trader.totalProfit) || 0,
      monthlyProfit: parseFloat(trader.monthlyProfit) || 0,
      winRate: parseFloat(trader.winRate) || 0
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      if (editingTrader) {
        await tradersAPI.update(editingTrader.id, values);
        message.success('交易员更新成功');
      } else {
        await tradersAPI.create(values);
        message.success('交易员创建成功');
      }
      
      setModalVisible(false);
      fetchTraders();
    } catch (error) {
      message.error('操作失败');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await tradersAPI.delete(id);
      message.success('交易员删除成功');
      fetchTraders();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete error:', error);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (isNaN(n)) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(2) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  // 上传头像
  const handleAvatarUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      const response = await tradersAPI.uploadAvatar(file);
      if (response.data.success && response.data.data.avatarUrl) {
        const url = response.data.data.avatarUrl;
        form.setFieldsValue({ avatar: url });
        message.success('头像上传成功');
        if (onSuccess) {
          onSuccess(response.data, file);
        }
      } else {
        const msg = response.data.message || '头像上传失败';
        message.error(msg);
        if (onError) {
          onError(new Error(msg));
        }
      }
    } catch (error) {
      console.error('Upload trader avatar error:', error);
      message.error('头像上传失败');
      if (onError) {
        onError(error);
      }
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 70,
      render: (rank) => (
        <span style={{ 
          fontWeight: 'bold', 
          color: rank <= 3 ? '#faad14' : '#666'
        }}>
          {rank ? `#${rank}` : '-'}
        </span>
      ),
    },
    {
      title: '交易员',
      key: 'trader',
      width: 220,
      render: (_, record) => (
        <Space>
          <Avatar 
            size={48} 
            src={getImageUrl(record.avatar)} 
            icon={<UserOutlined />}
            onError={(e) => {
              console.error('交易员头像加载失败:', {
                failedUrl: e.target.src,
                rawAvatar: record.avatar,
              });
            }}
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{record.title || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '总利润',
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      width: 120,
      render: (profit) => (
        <span style={{ 
          color: parseFloat(profit) >= 0 ? '#52c41a' : '#f5222d',
          fontWeight: 'bold'
        }}>
          ${formatNumber(profit)}
        </span>
      ),
    },
    {
      title: '总交易',
      dataIndex: 'totalTrades',
      key: 'totalTrades',
      width: 90,
    },
    {
      title: '胜率',
      dataIndex: 'winRate',
      key: 'winRate',
      width: 90,
      render: (rate) => (
        <Tag color={parseFloat(rate) >= 50 ? 'green' : 'orange'}>
          {parseFloat(rate).toFixed(1)}%
        </Tag>
      ),
    },
    {
      title: '月度利润',
      dataIndex: 'monthlyProfit',
      key: 'monthlyProfit',
      width: 110,
      render: (profit) => (
        <span style={{ 
          color: parseFloat(profit) >= 0 ? '#52c41a' : '#f5222d' 
        }}>
          ${formatNumber(profit)}
        </span>
      ),
    },
    {
      title: '关注',
      dataIndex: 'followers',
      key: 'followers',
      width: 80,
      render: (num) => formatNumber(num),
    },
    {
      title: '点赞',
      dataIndex: 'likes',
      key: 'likes',
      width: 80,
      render: (num) => formatNumber(num),
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '显示' : '隐藏'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="确定删除此交易员吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <TrophyOutlined style={{ marginRight: 8 }} />
            交易员排行榜管理
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加交易员
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={traders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingTrader ? '编辑交易员' : '添加交易员'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="name"
            label="交易员名称"
            rules={[{ required: true, message: '请输入交易员名称' }]}
          >
            <Input placeholder="请输入名称" />
          </Form.Item>

          <Form.Item
            name="avatar"
            label="头像图片URL"
          >
            <Input placeholder="请输入头像图片链接，或通过下方上传自动填写" />
          </Form.Item>

          <Form.Item label="上传头像">
            <Upload
              showUploadList={false}
              customRequest={handleAvatarUpload}
              accept="image/jpeg,image/png,image/gif,image/webp"
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? '上传中...' : '点击上传头像图片'}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar
                size={48}
                src={getImageUrl(form.getFieldValue('avatar'))}
                icon={<UserOutlined />}
              />
              <span style={{ color: '#999' }}>上传成功后，会自动填充上方头像URL，并在此处预览</span>
            </div>
          </Form.Item>

          <Form.Item
            name="title"
            label="专业头衔"
          >
            <Input placeholder="例如：高级交易员、策略分析师" />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="totalProfit"
              label="总利润 ($)"
              rules={[{ required: true, message: '请输入总利润' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="总利润金额"
                step={100}
              />
            </Form.Item>

            <Form.Item
              name="totalTrades"
              label="总交易次数"
              rules={[{ required: true, message: '请输入总交易次数' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                min={0}
                placeholder="交易次数"
              />
            </Form.Item>

            <Form.Item
              name="winRate"
              label="胜率 (%)"
              rules={[{ required: true, message: '请输入胜率' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                min={0}
                max={100}
                step={0.1}
                placeholder="0-100"
              />
            </Form.Item>

            <Form.Item
              name="monthlyProfit"
              label="月度利润 ($)"
              rules={[{ required: true, message: '请输入月度利润' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                step={100}
                placeholder="月度利润金额"
              />
            </Form.Item>

            <Form.Item
              name="followers"
              label="关注人数"
            >
              <InputNumber 
                style={{ width: '100%' }} 
                min={0}
                placeholder="关注人数"
              />
            </Form.Item>

            <Form.Item
              name="likes"
              label="点赞数"
            >
              <InputNumber 
                style={{ width: '100%' }} 
                min={0}
                placeholder="点赞数"
              />
            </Form.Item>

            <Form.Item
              name="rank"
              label="排名顺序"
            >
              <InputNumber 
                style={{ width: '100%' }} 
                min={1}
                placeholder="排名"
              />
            </Form.Item>

            <Form.Item
              name="isActive"
              label="是否显示"
              valuePropName="checked"
            >
              <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
            </Form.Item>
          </div>

          <Form.Item
            name="specialties"
            label="专长领域"
          >
            <Input placeholder="例如：股票, 期货, 外汇 (逗号分隔)" />
          </Form.Item>

          <Form.Item
            name="description"
            label="个人简介"
          >
            <TextArea rows={3} placeholder="请输入个人简介" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Traders;

